<template>
  <fts-design-modal
    width="1200px"
    class="fts-activity-design-modal"
    ref="ftsDesignModal">
    <!-- title -->
    <template #title>
      <div class="design-modal-header-std__title with-hint">
        <fts-emoji v-model="formData.emoji" class="design-modal-header-emoji" />
        <input 
          type="text" 
          placeholder="Please enter a name"
          class="fts-form-control design-modal-header-input"
          :class="{error: $v.formData.name && $v.formData.name.$error}" 
          :style="getStyle"
          v-model="formData.name"
          spellcheck="false"
          @focus="designNameHasFocus = true"
          @blur="designNameHasFocus = false"
          @input="dispatchAutofill"
          @keydown.enter="doneContinue"
          ref="designerName"
        >
        <div class="design-modal-header-hint" v-if="headerHintHelper">
          press enter to continue
        </div>
        <!-- color -->
        <swatches 
          class="design-modal-header-swatches"
          v-model="formData.color"
          row-length="7"
          :swatches="colors"
          shapes="circles"
          popover-x="right"
          :swatch-size="25"
          @input="selectColor"
        />
      </div>
      <!-- error message -->
      <div class="design-modal-form-error" v-if="error('name')">
        {{error('name')}}
      </div>
    </template>
    <!-- description -->
    <template #description>
      <span class="design-modal-header-tag">Activity</span>
      <span class="design-modal-header-description">
        designing an activity can be performed by user
      </span>
    </template>
    <!-- body -->
    <template #body>
      <!-- description -->
      <div class="fts-form-group">
        <fts-contenteditable
          v-model="formData.description"
          text-wrap
          placeholder="enter description (optional)"
        />
      </div>
      <!-- form design -->
      <fts-collapsible-element initial-visible class="dm-collapsible">
        <!-- collapsible header -->
        <div
          slot="toggle"
          slot-scope="{ isVisible, toggle }"
          class="dm-collapsible-header"
        >
          <div class="dm-collapsible-header-wrapper">
            <div class="dm-collapsible-header__title">
              <!-- title -->
              <div class="dm-collapsible-header-title">
                Form
              </div>
              <!-- tag form -->
              <fts-tippy content="Remove form" v-if="formData.design">
                <div slot="trigger" class="dm-collapsible-header-tag-form">
                  <i class="fts-icon-form tag-form-icon" />
                  <font-awesome-icon
                    :icon="['fas', 'times']"
                    class="tag-form-action"
                    @click="removeWorkDesign"
                  />
                </div>
              </fts-tippy>
              <!-- tag action -->
              <fts-tippy
                :content="formDesignPreview ? 'switch to design mode' : 'switch to preview mode'" 
                v-if="formData.design && isVisible"
              >
                <div slot="trigger" class="dm-collapsible-header-tag-action" @click="updateFormDesignMode">
                  <div class="dm-collapsible-header-tag-label">
                    {{formDesignPreview ? "Design" : "Preview"}}
                  </div>
                </div>
              </fts-tippy>
            </div>
            <!-- error -->
            <div
              class="dm-collapsible-header-error"
              v-if="$v.formData && $v.formData.design && $v.formData.design.$error"
            >
              Form cannot be empty. Form at least must have information or element.
            </div>
            <!-- description -->
            <div class="dm-collapsible-header-description">
              designing an activity can be performed by user.
            </div>
          </div>
          <!-- actions -->
          <div class="dm-collapsible-header-actions">
            <!-- control -->
            <font-awesome-icon
              :icon="['fas', 'caret-right']"
              fixed-width
              class="dm-collapsible-control"
              :class="{ 'dm-collapsible-control--active': isVisible }"
              @click="toggle"
            />
          </div>
        </div>
        <!-- collapsible content -->
        <div slot="content" class="dm-collapsible-content">
          <!-- form designer -->
          <fts-form-designer
            :classification="formData.classification"
            :form="formData.design"
            :activityId="formData.id"
            :formData="classificationFormData"
            @previewChanged="(x) => formDesignPreview = x"
            ref="ftsFormDesigner"
            v-if="formData.design"
          />
          <!-- create work design -->
          <div class="dm-form-design-create" @click="createWorkDesign" v-else>
            <i class="fts-icon-form dm-form-design-create-icon" />
            <div>Click to add form</div>
          </div>
        </div>
      </fts-collapsible-element>
      <!-- flow design -->
      <fts-collapsible-element initial-visible class="dm-collapsible">
        <!-- header -->
        <div
          slot="toggle"
          slot-scope="{ isVisible, toggle }"
          class="dm-collapsible-header"
        >
          <div class="dm-collapsible-header-wrapper">
            <div class="dm-collapsible-header__title">
              <!-- title -->
              <div class="dm-collapsible-header-title">
                Flow
              </div>
              <!-- flow diagram -->
              <fts-tippy content="Open flow diagram">
                <div slot="trigger" class="dm-collapsible-header-tag-action" @click="openWorkflow">
                  <font-awesome-icon :icon="['fas', 'project-diagram']" class="dm-collapsible-header-tag-icon" />
                  <div class="dm-collapsible-header-tag-label">
                    Show Diagram
                  </div>
                </div>
              </fts-tippy>
            </div>
            <!-- description -->
            <div class="dm-collapsible-header-description">
              <div>
                User creates an entry by submitting form above. Indicate below whether the entry has an inistate everytime an new entry created. You may always change the configuration in a later time.
              </div>
              <div>
                Hint: For entry that does not have a process, you may safely ignore. If it is a workflow or process, you may start by creating the initial state.
              </div>
            </div>
          </div>
          <!-- actions -->
          <div class="dm-collapsible-header-actions">
            <!-- control -->
            <font-awesome-icon
              :icon="['fas', 'caret-right']"
              fixed-width
              class="dm-collapsible-control"
              :class="{ 'dm-collapsible-control--active': isVisible }"
              @click="toggle"
            />
          </div>
        </div>
        <!-- content -->
        <template #content>
        <div class="dm-collapsible-content">      
            <!-- flows -->
            <template v-if="activityFlows && activityFlows.length">
              <div
                v-for="x in activityFlows" :key="x.id"
                class="fts-flow-display dm-flow-display"
              >
                <div class="dm-flow-wrapper">
                  <!-- from state -->
                  <fts-state-design-inline
                    v-model="x.fromStateId"
                    :list="states"
                    @state:selected="setFlow(x)"
                    @state:created="setFlow(x)"
                    @navigate="navigateStateDesign"
                    @navigate:activity="navigateNewActivityDesign"
                  />
                  <!-- direction -->
                  <font-awesome-icon :icon="['fas', 'long-arrow-alt-right']" class="dm-flow-display-direction" />
                  <!-- activity -->
                  <fts-label-group 
                    :item="{
                      emoji: formData.emoji,
                      name: formData.name,
                    }"
                    emoji
                  />
                  <!-- direction -->
                  <font-awesome-icon :icon="['fas', 'long-arrow-alt-right']" class="dm-flow-display-direction" />
                  <!-- to state -->
                  <template v-if="x.conditional">
                    <template v-for="(state, stateIndex) in x.conditionalStates">
                      <div :key="`or-${state.id}`" v-if="stateIndex" class="dm-flow-display-direction">
                        or
                      </div>
                      <fts-state-design-inline
                        :key="`state-${state.id}`"
                        v-model="state.toStateId"
                        :list="states"
                        @state:selected="setFlow(x)"
                        @state:created="setFlow(x)"
                        @navigate="navigateStateDesign"
                        @navigate:activity="navigateNewActivityDesign"
                        v-if="state.toStateId"
                      />
                    </template>
                  </template>
                  <fts-state-design-inline
                    v-model="x.toStateId"
                    :list="states"
                    @state:selected="setFlow(x)"
                    @state:created="setFlow(x)"
                    @navigate="navigateStateDesign"
                    @navigate:activity="navigateNewActivityDesign"
                    v-else
                  />
                </div>
                <div class="dm-flow-action">
                  <fts-btn icon text @click="removeFlow(x)">
                    <font-awesome-icon :icon="['far', 'trash-alt']" />
                  </fts-btn>
                </div>
              </div>     
            </template>
            <!-- new flow -->
            <div class="fts-flow-display dm-flow-display" v-if="flowFormData && flowFormData.id">
              <div class="dm-flow-wrapper">
                <!-- from state -->
                <fts-state-design-inline
                  :state-id="flowFormData.fromStateId"
                  :list="states"
                  :editable="false"
                  @navigate="navigateStateDesign"
                  @navigate:activity="navigateNewActivityDesign"
                  v-if="params && params.fromStateId"
                />
                <fts-state-design-inline
                  v-model="flowFormData.fromStateId"
                  :list="states"
                  initial-edit-mode
                  @navigate="navigateStateDesign"
                  @navigate:activity="navigateNewActivityDesign"
                  :key="`from-state-${flowFormData.id}`"
                  v-else
                />
                <!-- direction -->
                <font-awesome-icon :icon="['fas', 'long-arrow-alt-right']" class="dm-flow-display-direction" />
                <!-- activity -->
                <fts-label-group 
                  :item="{
                    emoji: formData.emoji,
                    name: formData.name || ('(activity unnamed)'),
                  }"
                  emoji
                  :class="{ 'fts-text-default': !formData.name }"
                />
                <!-- direction -->
                <font-awesome-icon :icon="['fas', 'long-arrow-alt-right']" class="dm-flow-display-direction" />
                <!-- to state -->
                <fts-state-design-inline
                  v-model="flowFormData.toStateId"
                  :list="states"
                  initial-edit-mode
                  @navigate="navigateStateDesign"
                  @navigate:activity="navigateNewActivityDesign"
                  :key="`to-state-${flowFormData.id}`"
                />
                <!-- error -->
                <div class="dm-flow-display-error" v-if="$v.flowFormData.$error">
                  From State and To State is required.
                </div>
              </div>
              <div class="dm-flow-action">
                <fts-btn icon text @click="clearFlow">
                  <font-awesome-icon :icon="['far', 'trash-alt']" />
                </fts-btn>
              </div>
            </div>
          </div>
          <!-- footer -->
          <div class="dm-collapsible-footer" v-if="isExist || !flowFormData">
            <fts-btn outline color="primary" text-transform-init @click="addFlow">
              <font-awesome-icon :icon="['fas', 'plus']" />Add new Flow
            </fts-btn>
          </div>
        </template>
      </fts-collapsible-element>
      <!-- advanced -->
      <fts-collapsible-element v-model="advanced" v-if="beta">
        <fts-btn 
          slot="toggle" 
          slot-scope="{isVisible, toggle}" 
          text 
          text-transform-init
          :class="{'open': isVisible}"
          class="design-modal-advanced-toggle" 
          @click="toggle()">
          Advanced settings <font-awesome-icon :icon="['fas', 'caret-right']" class="design-modal-advanced-toggle-icon" />
        </fts-btn>
        <div slot="content" class="design-modal-advanced-content fts-row gutters">
          <!-- script -->
          <div class="fts-col design-modal-form-group" v-if="formData.design">
            <fts-form-script :form-data="formData.design" />
          </div>
          <!-- internal -->
          <fts-form-element
            :form-data="formData"
            :item="{
              fieldName: 'internal',
              input: 'toggleSwitch',
            }"         
            comfort-design
            class="fts-col" />
          <!-- skip history -->
          <fts-form-element
            :form-data="formData"
            :item="{
              fieldName: 'skipHistory',
              input: 'toggleSwitch',
            }"
            comfort-design 
            class="fts-col" />
          <!-- disable bulk -->
          <fts-form-element
            :form-data="formData"
            :item="{
              fieldName: 'disableBulkActions',
              input: 'toggleSwitch',
            }" 
            comfort-design 
            class="fts-col" />
          <!-- menus -->
          <fts-form-element
            :formData="formData"
            :item="{
              'fieldName': 'menus',
              'displayName': 'Listing',
              'icon': 'fts-icon-menu-caret-down',
            }"
            comfort-design
            class="fts-col">
            <fts-select
              slot="formElement"
              slot-scope="{formData, item}"
              :form-data="formData"
              :item="item"
              :options="menus"
              multiple
              comfort-design>
              <fts-label-group
                slot="selected-option-container"
                slot-scope="{ option }"
                :item-id="option.id"
                :list="menus"
                emoji
              />
              <fts-label-group
                slot="option"
                slot-scope="{ option }"
                :item-id="option.id"
                :list="menus"
                emoji
              />
            </fts-select>
          </fts-form-element>
        </div>
      </fts-collapsible-element>
      <!-- state design modal -->
      <fts-state-design-modal ref="ftsStateDesignModal" />
    </template>
    <!-- footer:left -->
    <template #footerLeft>
      <!-- remove -->
      <fts-btn text icon @click="remove" v-if="isExist">
        <font-awesome-icon :icon="['far', 'trash-alt']" />
      </fts-btn>
      <!-- auth -->
      <fts-auth
        type="activity"
        :unit-id="formData.id"
        :title="formData.name"
        preview-placement="top"
      >
        <fts-btn
          slot="trigger"
          slot-scope="{ show, isRight }"
          text
          icon
          :color="isRight ? 'danger' : 'default'"
          @click="show"
        >
          <font-awesome-icon :icon="['fas', 'lock']" v-if="isRight" />
          <font-awesome-icon :icon="['fas', 'unlock']" v-else />
        </fts-btn>
      </fts-auth>
    </template>
    <!-- footer:right -->
    <template #footerRight>
      <!-- cancel -->
      <fts-btn text @click="beforeClose">Cancel</fts-btn>
      <!-- save -->
      <fts-btn color="primary" text @click="done(true)" v-if="isExist">
        <font-awesome-icon :icon="['fas', 'check']" /> Save
      </fts-btn>
      <!-- done -->
      <fts-btn outline color="primary" @click="done">
        <font-awesome-icon :icon="['fas', 'check']" /> Ok
      </fts-btn>
      <!-- done and continue -->
      <fts-btn outline color="primary" @click="doneContinue" v-if="continuable">
        <font-awesome-icon :icon="['fas', 'chevron-right']" /> Continue
      </fts-btn>
    </template>
  </fts-design-modal>
</template>

<script>
import Vue from "vue";
import Vuelidate from "vuelidate";
Vue.use(Vuelidate);
import { required } from "vuelidate/lib/validators";

import Swatches from "vue-swatches";
import changesets from "diff-json";

import commons from "../commons/commons";
import canvasDesign from "../commons/canvasDesign";
import validator from "../commons/designValidator";

import FTSDesignModal from "./FTSDesignModal";
import FTSAuth from "./FTSAuth";
import FTSBtn from "../elements/FTSBtn";
import FTSCollapsibleElement from "../elements/FTSCollapsibleElement";
import FTSFormElement from "../elements/FTSFormElement";
import FTSEmoji from "../elements/FTSEmoji";
import FTSSelect from "../elements/FTSSelect";
import FTSLabelGroup from "../elements/display/FTSLabelGroup";
import FTSFormDesigner from "../elements/FTSFormDesigner";
import FTSTransitionExpand from "../elements/FTSTransitionExpand";
import FTSTippy from "../elements/FTSTippy";
import FTSContenteditable from "../elements/FTSContenteditable";
import FTSFormScript from "./FTSFormScript";
import FTSState from "../elements/FTSState";
import FTSStateDesignModal from "./FTSStateDesignModal";
import FTSStateDesignInline from "./FTSStateDesignInline";

export default {
  components: {
    Swatches,
    "fts-design-modal": FTSDesignModal,
    "fts-auth": FTSAuth,
    "fts-btn": FTSBtn,
    "fts-collapsible-element": FTSCollapsibleElement,
    "fts-form-element": FTSFormElement,
    "fts-emoji": FTSEmoji,
    "fts-select": FTSSelect,
    "fts-label-group": FTSLabelGroup,
    "fts-form-designer": FTSFormDesigner,
    "fts-transition-expand": FTSTransitionExpand,
    "fts-tippy": FTSTippy,
    "fts-contenteditable": FTSContenteditable,
    "fts-form-script": FTSFormScript,
    "fts-state": FTSState,
    "fts-state-design-modal": FTSStateDesignModal,
    "fts-state-design-inline": FTSStateDesignInline,
  },
  data: () => ({
    formData: null,
    originalData: null,
    flowFormData: null,
    resolve: null,
    saveCallBack: null,
    continueCallBack: null,
    alternativeDesignCallback: null,
    navigateCallback: null,
    params: null,
    autofillLoading: false,
    designNameHasFocus: false,
    advanced: false,
    classificationFormData: {},
    formDesignPreview: false,
  }),
  validations() {
    let formData = {};
    let flowFormData = {};
    formData = validator.activityDesign(this.activities);
    flowFormData = {
      fromStateId: { required },
      toStateId: { required }
    }
    return { formData, flowFormData };
  },
  methods: {
    async show(item, saveCallBack, continueCallBack, navigateCallback, params) {
      this.formData = _.cloneDeep(item);
      this.saveCallBack = saveCallBack;
      this.continueCallBack = continueCallBack;
      this.navigateCallback = navigateCallback;  
      this.params = params;

      /* flow from state */
      if (this.params && this.params.fromStateId) {
        this.addFlow();
        this.$set(this.flowFormData, "fromStateId", this.params.fromStateId);
      }
      /* form design */
      if (this.formData.design && this.formData.design.rows) {
        commons.expandWorkDesign(this.information, this.formData.design);
        // set payload
        if (!this.formData.design.payload)
          this.$set(this.formData.design, "payload", {})
      } else if (this.formData.design) {
        this.$delete(this.formData, "design")
      }
  
      return new Promise(resolve => {
        this.resolve = resolve;
        this.$refs.ftsDesignModal.show({
          beforeCloseHandler: () => {
            const diff = changesets.diff(this.formData, this.originalData)
            if (diff && diff.length) {
              this.$modal.show("dialog", {
                title: "Confirm discard changes",
                text: "Discard all changes?",
                buttons: [
                  { title: "Cancel" },
                  {
                    title: "Discard",
                    default: true,
                    handler: () => {
                      this.$modal.hide("dialog");
                      this.reset();
                    },
                  },
                ],
              });             
            } else {
              this.reset();
            }
          },
        });

        this.$nextTick(() => {
          this.originalData = _.cloneDeep(this.formData);
          this.$refs.designerName.focus();
          this.designNameHasFocus = true;
        });
      })
    },
    done(save) {
      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        const formData = _.cloneDeep(this.formData);
        const alternativeDesign = this.getAlternativeDesign();
        if (save) {
          this.saveCallBack && this.saveCallBack({...formData, ...alternativeDesign});
          this.originalData = _.cloneDeep(this.formData);
        } else {
          this.resolve && this.resolve({
            formData: formData,
            alternativeDesign: alternativeDesign,
          });
          this.reset();
        }     
      }
    },
    doneContinue() {
      if (!this.continuable) return;

      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        const formData = _.cloneDeep(this.formData);
        const continueCallBack = Object.assign(this.continueCallBack);
        const alternativeDesign = this.getAlternativeDesign();
        /* reset */
        this.reset();
        continueCallBack && continueCallBack(formData, alternativeDesign);
        this.$toasted.info(`Activity: ${formData.name} is added.`);
      }
    },
    navigateContinue(type, item, params) {
      let alternativeDesign = null;

      function navigate(vm) {
        const formData = _.cloneDeep(vm.formData);
        const navigateCallback = Object.assign(vm.navigateCallback);
        /* reset */
        vm.reset();
        /* navigate */
        navigateCallback && navigateCallback(
          formData,
          type,
          item,
          params,
          alternativeDesign,
        );
      }
      /* set alternative design */
      alternativeDesign = this.getAlternativeDesign();

      this.$v.formData.$touch();
      if (!this.$v.formData.$invalid) {
        if (this.isExist) {
          const diff = changesets.diff(this.formData, this.originalData);
          if (diff && diff.length) {
            this.$modal.show("dialog", {
              title: "Confirmation",
              text: "Keep changes and continue?",
              buttons: [
                { title: "Cancel" },
                {
                  title: "Continue",
                  default: true,
                  handler: () => {
                    this.$modal.hide("dialog");
                    navigate(this);
                  },
                },
              ],
            });
          } else {
            navigate(this);
          }
        } else {
          navigate(this);
        }
      }
    },
    beforeClose() {
      this.$refs.ftsDesignModal.beforeClose();
    },
    remove() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete ${this.formData.name}?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.resolve && this.resolve(true);
              this.reset();
            },
          },
        ],
      });  
    },
    reset() {
      this.$v.$reset();
      this.$refs.ftsDesignModal.close();
      Object.assign(this.$data, this.$options.data.apply(this));
      /* set route */
      if (this.$route.query.t || this.$route.query.tid) {
        let query = {...this.$route.query};
        delete query.t;
        delete query.tid;
        this.$router.push({ query: query })
      }
    },
    debounceInput: _.debounce((vm) => {
      vm.autofillLoading = false;
    }, 800),
    dispatchAutofill() {
      if (!this.autofillLoading)
        this.autofillLoading = true;
      
      this.debounceInput(this);
    },
    error(key) {
      return validator.activityDesignError(this.$v.formData, key);
    },
    createWorkDesign() {
      this.$set(this.formData, "design", {
        rows: [{index: 0, items: []}],
        backendScript: "",
        payload: "{}",
      });
    },
    removeWorkDesign() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: "Remove form?",
        buttons: [
          { title: "Cancel" },
          {
            title: "Remove",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.$delete(this.formData, "design");
            },
          },
        ],
      });  
    },
    updateFormDesignMode() {
      this.$refs.ftsFormDesigner && this.$refs.ftsFormDesigner.previewDesign();
    },
    selectColor(val) {
      if (!val) this.$delete(this.formData, 'color');
    },
    clearFlow() {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete flow?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              this.flowFormData = null;
            }
          }
        ]
      });
    },
    setFlow(item){
      if (item && item.id) {
        let canvas = _.cloneDeep(this.$store.state.canvas);
        canvasDesign.setFlow(canvas, item);
        this.$store.dispatch("updateVectorCanvas", canvas);
      }
    },
    addFlow() {
      if (this.flowFormData && this.flowFormData.id) {
        this.$v.flowFormData.$touch();
        if (!this.$v.flowFormData.$invalid) {
          const submitData = _.cloneDeep(this.flowFormData);

          function continueFlow(vm) {
            /* set flow */
            vm.setFlow(submitData);
            /* reset */
            vm.$v.flowFormData.$reset();
            vm.flowFormData = {
              ...canvasDesign.initFlow(),
              activityId: vm.formData.id,
            }
          }

          if (this.isExist) {    
            this.$modal.show("dialog", {
              title: "Confirmation",
              text: "Submit new flow and continue?",
              buttons: [
                { title: "Cancel" },
                {
                  title: "Continue",
                  default: true,
                  handler: () => {
                    this.$modal.hide("dialog");
                    continueFlow(this);
                  },
                },
              ],
            });
          } else {
            continueFlow(this);
          }
        }
      } else {
        this.flowFormData = {
          ...canvasDesign.initFlow(),
          activityId: this.formData.id,
        }
      }
    },
    removeFlow(item) {
      this.$modal.show("dialog", {
        title: "Confirmation",
        text: `Delete flow?`,
        buttons: [
          { title: "Cancel" },
          {
            title: "Delete",
            default: true,
            handler: () => {
              this.$modal.hide("dialog");
              let canvas = _.cloneDeep(this.$store.state.canvas);
              canvasDesign.deleteFlow(canvas, item);
              this.$store.dispatch("updateVectorCanvas", canvas);
            }
          }
        ]
      });
    },
    navigateStateDesign(id) {
      const result = this.states.find(x => x.id === id);
      if (result)
        this.navigateContinue(canvasDesign.state, result);
      else
        this.$toasted.error('Something went wrong. State not found.');
    },
    navigateNewActivityDesign(id) {
      const params = { fromStateId: id, disableContinue: true };
      this.navigateContinue(canvasDesign.activity, null, params);
    },
    getAlternativeDesign() {
      let submitData = null;

      /* set flow data */
      if (this.flowFormData && this.flowFormData.fromStateId) {
        if (!submitData)
          submitData = [];

        let flow = _.cloneDeep(this.flowFormData);
        if (!flow.toStateId)
          flow.toStateId = flow.fromStateId;

        submitData.push({
          type: canvasDesign.flow.name,
          extension: flow, 
        });
      }

      return submitData;
    },
    openWorkflow() {
      this.$openStudioWorkflow();
    },
  },
  computed: {
    beta() {
      return this.$store.state.beta;
    },
    information() {
      return this.$store.getters.canvasInformation;
    },
    activities() {
      return this.$store.getters.canvasActivities;
    },
    menus() {
      return this.$store.getters.canvasMenus.map(x => ({
        id: x.id,
        name: x.name,
        emoji: x.emoji,
      }));
    },
    states() {
      return this.$store.getters.canvasStates;
    },
    colors() {
      return commons.darkPalette.concat([""]);
    },
    isExist() {
      return Boolean(this.activities.find(x => x.id === this.formData.id));
    },
    getStyle() {
      if (this.formData && this.formData.name && this.formData.color)
        return {
          "color": this.formData.color,
        }
    },
    continuable() {
      return !this.isExist && Boolean(this.params && this.params.disableContinue) !== true
    },
    headerHintHelper() {
      return (
        this.continuable &&
        this.designNameHasFocus &&
        !this.autofillLoading &&
        !this.isExist &&
        this.formData &&
        this.formData.name &&
        this.$v.formData &&
        !this.$v.formData.$error
      );
    },
    activityFlows() {
      return this.$store.getters.canvasFlows.filter(x => x.activityId === this.formData.id);
    },
  }
}
</script>